import {
  get,
  dropRight,
  forEach,
  camelCase,
  cloneDeep,
  merge,
  intersection,
  keys,
  isEmpty,
  omit,
  each,
  pick,
} from 'lodash'
import Cookies from 'universal-cookie'
import { createAction } from 'redux-actions'
import { replace, push } from 'connected-react-router'
import log from 'blazar-sentry'
import queryString from 'query-string'
import { actionCreators as geoPickerActionCreators } from 'reduxBits/geoPicker'
import devicePlugin, { isDevice } from '@blazar-plugins/device'

import { getTreeNodes, getAllQueryKeys, getGeoParams } from 'utils/parameters'
import getCurrentUrl, { buildListUrl, buildListPathname } from 'utils/url'
import domainName from 'utils/domainName'

import { actionCreators as api } from 'reduxBits/api'
import { actionCreators as classifiedActions } from 'reduxBits/classified'

export const REQUEST_PARAMETERS = 'Send an async param request to server'
export const RECEIVE_PARAMETERS = 'Update the state with the fetched param information'
export const REQUEST_ACTIVE_PARAMETERS = 'Request all the active parameters'
export const RECEIVE_ACTIVE_PARAMETERS = 'Receive all the active parameters'
export const SAVE_FILTER_PARAMETERS = 'Save filter parameters'
export const UPDATE = 'Update some data in the searchParams store'
export const NO_UPDATE_PARAMETERS = 'No update of parameters'
export const SHOW_ADS = 'Show ads'
export const CLEAR_FILTER = 'Clear filter'
export const SET_DECIDING_KEYS = 'Set the deciding keys'
export const SEARCH_RESULT_DESCRIPTION_CLICKED = 'Search result description clicked'
export const CATEGORY_LINK_CLICKED = 'Category link clicked'

const update = createAction(UPDATE)
const noUpdateParameters = createAction(NO_UPDATE_PARAMETERS)
const requestParameters = createAction(REQUEST_PARAMETERS)
const receiveParameters = createAction(RECEIVE_PARAMETERS)
const requestActiveParameters = createAction(REQUEST_ACTIVE_PARAMETERS)
const receiveActiveParameters = createAction(RECEIVE_ACTIVE_PARAMETERS)
const showAds = createAction(SHOW_ADS)
const clearFilter = createAction(CLEAR_FILTER)
const saveFilterParameters = createAction(SAVE_FILTER_PARAMETERS)
const setDecidingKeys = createAction(SET_DECIDING_KEYS)
const searchResultDescriptionClicked = createAction(SEARCH_RESULT_DESCRIPTION_CLICKED)
const categoryLinkClicked = createAction(CATEGORY_LINK_CLICKED)

const getLocation = (getState) => getState().router.location
const getQuery = (getState) => queryString.parse(get(getLocation(getState), 'search', {}))
const getCurrentFilterQuery = (getState) => ({
  ...getQuery(getState),
  ...get(getLocation(getState), 'state.searchModal.query', {}),
})

function handleResponse(response) {
  if (!response.ok) {
    throw new Error(`Failed request to ${response.url}`)
  }

  return response.textOrJson
}

const fetchParameters = () => async (dispatch) => {
  let parameters = []
  let activeParameters = []
  let dataGroups = {}
  let decidingKeys = []

  dispatch(requestParameters())

  const parametersFetch = dispatch(api.legacyApiFetch('blocket/v6/parameters/parameter_map'))
  const dataGroupsFetch = dispatch(api.legacyApiFetch('blocket/v6/parameters/data_group_map'))
  const activeParametersFetch = dispatch(
    api.legacyApiFetch('blocket/v6/parameters/active?context=search'),
  )
  const decidingKeysFetch = dispatch(
    api.legacyApiFetch('blocket/v6/parameters/deciding_keys?context=search'),
  )

  try {
    ;[{ parameters }, { dataGroups }, { activeParameters }, { decidingKeys }] = (await Promise.all([
      parametersFetch,
      dataGroupsFetch,
      activeParametersFetch,
      decidingKeysFetch,
    ])).map(handleResponse)
  } catch (error) {
    log.error(error)
    dispatch(receiveParameters(error))
    return
  }

  forEach(parameters, (parameter) => {
    if (parameter.rangeStart && parameter.rangeEnd) {
      parameter.rangeStart.items = dataGroups[camelCase(parameter.rangeStart.dataGroup)].items
      parameter.rangeEnd.items = dataGroups[camelCase(parameter.rangeEnd.dataGroup)].items

      if (parameter.rangeStart.dataGroupOrder) {
        parameter.rangeStart.items = parameter.dataGroupOrder.map(
          (dataIndex) => parameter.rangeStart.items[dataIndex],
        )
      }
      if (parameter.rangeEnd.dataGroupOrder) {
        parameter.rangeEnd.items = parameter.dataGroupOrder.map(
          (dataIndex) => parameter.rangeEnd.items[dataIndex],
        )
      }
    }

    if (parameter.dataGroup) {
      parameter.items = dataGroups[camelCase(parameter.dataGroup)].items

      if (parameter.dataGroupOrder) {
        parameter.items = parameter.dataGroupOrder.map((dataIndex) => parameter.items[dataIndex])
      }
    }
  })

  dispatch(receiveParameters(parameters))
  dispatch(
    receiveActiveParameters(activeParameters.map((parameter) => parameters[camelCase(parameter)])),
  )
  dispatch(setDecidingKeys(decidingKeys))
}

const updateActiveParameters = () => async (dispatch, getState) => {
  dispatch(requestActiveParameters())

  let activeParameters = []
  const washedQuery = omit(getQuery(getState), 'q') // If "q" is set, remove it as it's not requested by API and will impact caching if present
  const encodedQueryString = encodeURIComponent(queryString.stringify(washedQuery))
  const url = `blocket/v6/parameters/active?context=search&qs=${encodedQueryString}`

  try {
    ;({ activeParameters } = handleResponse(await dispatch(api.legacyApiFetch(url))))
  } catch (error) {
    log.error(error)
    dispatch(receiveActiveParameters(error))
    return
  }

  const { parameters } = getState().searchParams

  dispatch(
    receiveActiveParameters(activeParameters.map((parameter) => parameters[camelCase(parameter)])),
  )
}

// Do not select a node, navigate to the clicked node instead
const explore = ({ newQueryValues }) => (dispatch, getState) => {
  const location = cloneDeep(getLocation(getState))

  const scrollHistory = get(location, 'state.searchModal.scrollHistory', [])
  const queryHistory = get(location, 'state.searchModal.queryHistory', [])

  scrollHistory.push([window.scrollX, window.scrollY])
  queryHistory.push(newQueryValues)

  location.state = {
    searchModal: {
      scrollHistory,
      queryHistory,
      query: merge({}, ...queryHistory),
    },
  }

  return Promise.resolve(dispatch(replace(location))).then(() =>
    dispatch(classifiedActions.fetchSearchTotal(getCurrentFilterQuery(getState))),
  )
}

const showFilteredAds = () => (dispatch, getState) => {
  // get the query string but remove the page parameter since this is a new search
  const query = omit(getQuery(getState), 'page')

  Promise.all([
    dispatch(showAds()),
    dispatch(geoPickerActionCreators.setGeoPreference(getGeoParams(query))),
    dispatch(replace(buildListUrl(query, getState().searchParams.byQueryKey))),
  ])
}

const goHome = (replaceOpts = {}) => (dispatch, getState) => {
  const location = cloneDeep(getLocation(getState))
  const scrollHistory = get(location, 'state.searchModal.scrollHistory', [0])
  const scrollTo = scrollHistory[0]

  return Promise.resolve(
    dispatch(
      replace({
        ...location,
        pathname: '/filter',
        state: {
          scrollTo,
        },
        ...replaceOpts,
      }),
    ),
  ).then(() => dispatch(noUpdateParameters()))
}

const goBackInTree = () => (dispatch, getState) => {
  const location = cloneDeep(getLocation(getState))
  const scrollHistory = get(location, 'state.searchModal.scrollHistory')

  /**
   * happens when you reload when navigated into a tree
   * doesn't navigate you to the right place exactly but it's better than crashing
   */
  if (!scrollHistory) {
    return dispatch(goHome())
  }

  location.state.scrollTo = scrollHistory.pop()

  if (get(location, 'state.searchModal.queryHistory.length', 0) > 0) {
    location.state.searchModal.queryHistory.pop()
    location.state.searchModal.query = merge({}, ...location.state.searchModal.queryHistory)
  } else {
    return dispatch(goHome())
  }

  return Promise.resolve(dispatch(replace(location))).then(() =>
    dispatch(classifiedActions.fetchSearchTotal(getCurrentFilterQuery(getState))),
  )
}

const goToParameter = ({ parameter }) => (dispatch, getState) => {
  const location = cloneDeep(getLocation(getState))
  const query = queryString.parse(location.search)
  const searchModal = {}

  const treeNodes = getTreeNodes(parameter, query)
  const lastNodeItems = get(treeNodes[treeNodes.length - 1], 'items')

  const nodes =
    lastNodeItems && parameter.id !== 'locations_single' ? treeNodes : dropRight(treeNodes)

  searchModal.queryHistory = nodes
    .filter((node) => node.value)
    .map((node) => ({
      [node.queryKey]: node.value,
    }))

  searchModal.query = merge({}, ...searchModal.queryHistory)
  searchModal.scrollHistory = new Array(searchModal.queryHistory.length).fill([0, 0])
  searchModal.scrollHistory.unshift([window.scrollX, window.scrollY])

  return Promise.resolve(
    dispatch(
      replace({
        ...location,
        pathname: `${location.pathname}/${parameter.label}`,
        state: {
          searchModal,
        },
      }),
    ),
  ).then(() => (parameter.id !== 'locations_single' ? dispatch(noUpdateParameters()) : null))
}

const select = (newQueryValues, options = {}) => async (dispatch, getState) => {
  const location = cloneDeep(getLocation(getState))
  const query = cloneDeep(getQuery(getState))

  let newQuery = {
    ...query,
    ...newQueryValues,
  }

  const changedDecidingKeys = intersection(
    getState().searchParams.decidingKeys,
    keys(newQueryValues),
  )

  // If a decidingKey has changed then the following parameters will have
  // their values dropped.
  if (!isEmpty(changedDecidingKeys)) {
    let keepKeys = ['label']

    each(getState().searchParams.activeParameters, (parameter) => {
      const queryKeys = getAllQueryKeys(parameter)
      keepKeys = keepKeys.concat(queryKeys)

      if (!isEmpty(intersection(changedDecidingKeys, queryKeys))) {
        return false
      }

      return true
    })

    newQuery = pick(newQuery, keepKeys)
  }

  if (options.goHome) {
    dispatch(goHome({ search: queryString.stringify(newQuery) }))
  } else {
    const pathname = options.updateListPathname
      ? buildListPathname(newQuery, getState().searchParams.byQueryKey)
      : location.pathname

    dispatch(
      replace({
        ...location,
        pathname,
        search: queryString.stringify(newQuery),
        state: {
          ...location.state,
          dontScroll: true,
        },
      }),
    )
  }
  /* Save selected filters in state */
  const cg = get(newQuery, 'cg')
  const cookies = new Cookies()
  if (cg === '1020') {
    /* using cookies for persistance */
    const cookieOptions = {
      path: '/',
      secure: false,
      httpOnly: false,
      maxAge: 60 * 60 * 24, // 24 hours
      domain: `.${domainName.getRootDomain(getCurrentUrl())}`,
    }

    const reducedFilteredQuery = omit(newQuery, ['cb', 'cg', 'cbl1', 'cbl2', 'q'])
    cookies.set('filterQuery', reducedFilteredQuery, cookieOptions)
    dispatch(saveFilterParameters(reducedFilteredQuery))
  }

  const currentScreenType = devicePlugin.selectors.screenType(getState())

  await dispatch(updateActiveParameters())
  if (!isDevice('medium', currentScreenType)) {
    await dispatch(classifiedActions.fetchSearchTotal(getQuery(getState)))
  }
}

const clearQuery = ({ keepKeys }) => (dispatch, getState) => {
  const query = getQuery(getState)
  const newQuery = {}
  keepKeys.forEach((key) => {
    newQuery[key] = query[key]
  })

  const newLocation = cloneDeep(getLocation(getState))
  newLocation.search = queryString.stringify(newQuery)
  dispatch(replace(newLocation))

  dispatch(clearFilter())
  dispatch(updateActiveParameters())
  dispatch(classifiedActions.fetchSearchTotal(getQuery(getState)))
}

const searchResultDescriptionClick = () => (dispatch, getState) => {
  const location = getLocation(getState)
  dispatch(
    push({
      ...location,
      pathname: '/filter',
    }),
  )

  dispatch(searchResultDescriptionClicked())
}

export const actionCreators = {
  goHome,
  update,
  select,
  explore,
  clearQuery,
  goToParameter,
  goBackInTree,
  fetchParameters,
  updateActiveParameters,
  showFilteredAds,
  searchResultDescriptionClick,
  categoryLinkClicked,
  saveFilterParameters,
}
